<script lang="ts" setup>
  import { useWindowSize } from '@vueuse/core'
  import WaveSurfer from 'wavesurfer.js'
  import useBem from '~/composables/Bem/Bem'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useIsMounted from '~/composables/IsMounted/IsMounted'
  import { BtnPlayToggleFacet } from '~/components/BtnPlayToggle/BtnPlayToggleFacet'

  interface Props extends UseBemProps {
    src: string
    facets?: Array<string>
  }

  interface Colors {
    waveColor: string
    progressColor: string
    cursorColor: string
  }

  const toHHMMSS = (s: number) => {
    const sNumber = parseInt(`${s}`, 10)
    const hours = Math.floor(sNumber / 3600)
    const minutes = Math.floor(sNumber / 60) % 60
    const seconds = sNumber % 60

    return [hours, minutes, seconds]
      .map((v) => (v < 10 ? '0' + v : v))
      .filter((v, i) => v !== '00' || i > 0)
      .join(':')
  }

  let resizeHandler: () => void

  const rootEl = ref<HTMLDivElement | null>(null)

  const props = defineProps<Props>()
  const { width, height } = useWindowSize()
  const { bemAdd } = useBem('c-wave-surfer', props, {})
  const { isMounted } = useIsMounted()

  const options = ref<any | null>(null)
  const waveEl = ref<HTMLDivElement | null>(null)
  const Instance = ref<WaveSurfer | null>(null)

  const progress = ref<number>(0)
  const duration = ref<number>(0)

  const progressText = computed<string>(() => {
    return `${toHHMMSS(progress.value)}`
  })
  const durationText = computed<string>(() => {
    return `${toHHMMSS(duration.value)}`
  })

  const isPlaying = computed<boolean>(() => {
    return !!Instance.value?.isPlaying()
  })

  const colors = computed<Colors>(() => {
    const computedStyle = window.getComputedStyle(rootEl.value as HTMLDivElement)

    return {
      waveColor: computedStyle.getPropertyValue('--c-wave-surfer--wave-color'),
      progressColor: computedStyle.getPropertyValue('--c-wave-surfer--progress-color'),
      cursorColor: computedStyle.getPropertyValue('--c-wave-surfer--cursor-color'),
    }
  })

  const resize = () => {
    Instance.value?.empty()
    Instance.value?.drawBuffer()
  }

  const toggle = () => {
    Instance.value?.playPause()
  }

  onMounted(() => {
    if (!waveEl.value) {
      return
    }

    options.value = {
      height: waveEl.value.offsetHeight,
      width: waveEl.value.offsetWidth,
      hideScrollbar: true,
      barWidth: 2,
      barMinHeight: 1,
      barGap: 0,
      normalize: true,
      fillParent: true,
      cursorWidth: 2,
      container: waveEl.value,
      ...colors.value,
    }

    Instance.value = WaveSurfer.create(options.value)
    resizeHandler = Instance.value.util.debounce(resize)

    if (!Instance.value) {
      return
    }

    Instance.value.load(props.src)
    Instance.value.on('ready', () => {
      duration.value = Instance.value?.getDuration() || 0
    })
    Instance.value.on('audioprocess', function (v) {
      progress.value = v
    })
    Instance.value.on('seek', function (v) {
      progress.value = duration.value * v
    })
  })

  onBeforeUnmount(() => {
    Instance.value?.destroy()
  })

  watch(
    [width, height],
    () => {
      if (isMounted.value && Instance.value) {
        Instance.value.pause()
        resizeHandler()
      }
    },
    { immediate: true },
  )
</script>

<template>
  <div ref="rootEl" class="c-wave-surfer">
    <div class="c-wave-surfer__controls">
      <BtnPlayToggle
        :facets="[BtnPlayToggleFacet.InAudio, BtnPlayToggleFacet.Dark]"
        :is-playing="isPlaying"
        @click="toggle"
      />
    </div>
    <div class="c-wave-surfer__display">
      <div ref="waveEl" class="c-wave-surfer__waveform" />
      <div class="c-wave-surfer__text">
        <span class="c-wave-surfer__progress">
          {{ progressText }}
        </span>
        <div class="c-wave-surfer__duration">
          {{ durationText }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/base/typography/typography' as type;

  $height: 40px;

  .c-wave-surfer {
    display: flex;
    width: 100%;
    column-gap: 20px;

    --c-wave-surfer--wave-color: #{col.$monochrome-lead};
    --c-wave-surfer--progress-color: #{col.$variant-cyan-dark};
    --c-wave-surfer--cursor-color: #{col.$variant-red-dark};
  }

  .c-wave-surfer__display {
    flex: 0 1 100%;
  }

  .c-wave-surfer__waveform {
    position: relative;
    display: block;
    width: 100%;
    height: $height;
  }

  .c-wave-surfer__controls {
    display: flex;
    flex: 0 0 $height;
    justify-content: center;
    align-items: center;
    width: $height;
    height: $height;
  }

  .c-wave-surfer__text {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
  }

  .c-wave-surfer__progress,
  .c-wave-surfer__duration {
    @include type.copy-x-small;
  }
</style>
